import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { SendIcon, StopIcon } from './icons.tsx';

const MAX_LENGTH = 2000;

export function Composer({
  busy,
  onSend,
  onStop,
}: {
  busy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
}) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (busy || !text.trim()) return;
    onSend(text);
    setText('');
    // Keep the keyboard closed on mobile after sending: the answer needs the screen
    ref.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) submit(e);
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-2xl px-3 pt-2.5 pb-1.5">
      <label htmlFor="question" className="sr-only">
        Tu pregunta
      </label>
      {/* One field with the button inside: the obvious place to start on a phone */}
      <div className="flex items-end gap-1.5 rounded-[1.6rem] border border-purple/25 bg-paper p-1.5 ps-4 shadow-[0_1px_2px_rgb(72_32_82/0.06)] transition-colors focus-within:border-gold-700 dark:border-cream/20 dark:bg-night-700 dark:focus-within:border-gold">
        <textarea
          id="question"
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={MAX_LENGTH}
          enterKeyHint="send"
          placeholder="Pregunta por una hermandad, calle u horario..."
          className="field-sizing-content max-h-36 min-h-11 flex-1 resize-none bg-transparent py-2.5 text-[1.0625rem] leading-6 text-ink placeholder:text-ink-soft/75 focus:outline-none focus-visible:outline-none dark:text-cream dark:placeholder:text-cream/50"
        />
        {busy ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Detener la respuesta"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ink-soft text-cream transition active:scale-95 dark:bg-cream/80 dark:text-ink"
          >
            <StopIcon />
          </button>
        ) : (
          <button
            type="submit"
            aria-label="Enviar pregunta"
            disabled={!text.trim()}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-purple text-cream transition hover:bg-purple-500 active:scale-95 disabled:bg-purple/35 disabled:active:scale-100 dark:bg-gold-300 dark:text-night dark:hover:bg-gold dark:disabled:bg-cream/15 dark:disabled:text-cream/45"
          >
            <SendIcon />
          </button>
        )}
      </div>
    </form>
  );
}
